import type { Accessor, AccessorWithIndex } from '../function';
import { isAccessor, isFunction } from '../function';
import type { Optional } from '../object';
import { isNullOrUndefined } from '../object';
import type { SumByReturn } from './number-types/sum-by-return';

const MAXIMUM_FRACTION_DIGITS = 2;

export function formatNumber(num: number | undefined, maximumFractionDigits = MAXIMUM_FRACTION_DIGITS): string {
  const numValue = num ?? 0;
  const minimumFractionDigits = getMinimumFractionDigits(numValue);
  const cappedMinimumFractionDigits = Math.min(minimumFractionDigits, maximumFractionDigits);

  return numValue.toLocaleString('en-US', { minimumFractionDigits: cappedMinimumFractionDigits, maximumFractionDigits });
}

function getMinimumFractionDigits(number: number) {
  if (Number.isInteger(number)) {
    return 0;
  }

  const fractionalPart = number.toString().split('.')[1];
  const startingZeroesCount = Array.from(fractionalPart).findIndex(digit => digit !== '0');

  return Math.max(startingZeroesCount + 1, 2);
}

export function parseNumber(numStr: Optional<string>): number | undefined {
  const parsedNumber = Number(numStr);
  
  return isNaN(parsedNumber) ? undefined : parsedNumber;
}
  
export function isNumber(value: unknown): value is number {
  return typeof value === 'number';
}

export function isNotNumber(value: unknown) {
  return !isNumber(value);
}

export function validateNumber(value: unknown): asserts value is number {
  if (isNotNumber(value)) {
    throw new Error('Value is not a number');
  }
}

export function sumBy<Item>(items: Item[] | undefined, accessor: Accessor<Item, number>): number;
export function sumBy<Item>(items: Item[] | undefined, accessor: AccessorWithIndex<Item, number>): number;
export function sumBy<Item, Key extends keyof Item>(items: Item[] | undefined, key: Key): SumByReturn<Item, Key>;
export function sumBy<Item>(items: Item[] | undefined, keyOrAccessor: keyof Item | Accessor<Item, number> | AccessorWithIndex<Item, number>): number {
  if (isNullOrUndefined(items)) {
    return 0;
  }

  const getNumericalValue = (item: Item, index: number) => (    
    isFunction(keyOrAccessor)
      ? (
        isAccessor<Item, number>(keyOrAccessor) 
          ? keyOrAccessor(item)
          : keyOrAccessor(item, index)
      )
      : item[keyOrAccessor] as number
  );
  
  return items.reduce<number>(
    (total, item, itemIndex) => getNumericalValue(item, itemIndex) + total,
    0,
  );
}

export function isBetweenExclusive(value: number, min: number, max: number): boolean {
  return value > min && value < max;
}

export function isBetweenInclusive(value: number, min: number, max: number): boolean {
  return value >= min && value <= max;
}

export function sum(...numbers: (number | undefined)[]) {
  return numbers.reduce<number>((total, num) => total + (num ?? 0), 0);
}

export function isBiggerThan(num: number, threshold: number): boolean {
  return num > threshold;
}

export function isInTheMillions(num: number): boolean {
  return isBiggerThan(num, 10 ** 6);
}

export function safeDivision(numerator: number, denominator: number): number {
  return denominator === 0 ? 0 : numerator / denominator;
}
export function calculatePercentageFromNumber(percentageAmount: number, amount: number) {
  return (percentageAmount / 100) * amount;
}
export function percentage(numerator: number, denominator: number): number {
  return safeDivision(numerator, denominator) * 100;
}

export function isPositive(value: number): boolean {
  return value > 0;
}

export function isNegative(value: number): boolean {
  return value < 0;
}

export function percentageRatio(numerator: number): number {
  return numerator / 100;
}

export function getValuesBetweenIncluding(min: number, max: number, step = 1): number[] {
  const values = [];

  for (let i = min; i <= max; i += step) {
    values.push(i);
  }

  return values;
}